import { useEffect, useRef, useState } from "react";
import { useHistory } from "react-router-dom";

import { HowWasAsk } from "../components/HowWasIt";
import { PlayerShell, usePlayerOrigin } from "../components/PlayerShell";
import { SegmentBar } from "../components/SegmentBar";
import { Tag } from "../components/Tag";
import { I } from "../components/icons";
import { dayLabel, fmtClock, todayStr } from "../domain/date";
import { findProgram, programToPhases } from "../domain/program";
import { quote } from "../domain/progress";
import { treadmillEndsAt, treadmillPosition } from "../domain/session";
import { tmProtocol, tmTotals } from "../domain/treadmill";
import type { TreadmillPhase } from "../domain/types";
import { strideMeters } from "../domain/energy";
import { pingSession } from "../lib/appBridge";
import { beep, buzz, cancelNotification, scheduleNotification, setWakeLock } from "../lib/device";
import { useTimer } from "../lib/useTimer";
import { useEasyTreadmill, useKaya } from "../store/kayaStore";

/**
 * The treadmill player.
 *
 * The protocol, the phase and the current phase's deadline live in the store's session record as
 * well as here, so the page can be closed, relaunched or left for the counter app and come back
 * to the same session — the machine kept going, and the record says how far. Without that, every
 * mount was a fresh session from phase one, with a fresh `start` to the counter app on top of the
 * one it was still holding. (ADR-027)
 */
export default function TreadRun() {
  const history = useHistory();
  const { S, update, checkBadges, session, beginSession, patchSession, endSession } = useKaya();
  const today = todayStr();
  /** Minimise, Close and End go back to the page the run was started from. */
  const origin = usePlayerOrigin("/train/treadmill");

  const existing = session?.kind === "treadmill" ? session : null;
  // One session at a time: reached with another kind open, hand over to it.
  const other = session && session.kind !== "treadmill" ? session : null;
  /** A resumed session re-announces itself to the counter app on the first tap — a mount
   *  cannot, because Chrome drops an `intent:` launch that no gesture asked for. */
  const announced = useRef(!existing);
  /** A session that ended while the page was away could not send `stop` (same reason); the
   *  next tap does. */
  const stopPending = useRef(false);
  // A machine program may be requested by id; otherwise this is the guided Kaya protocol. A
  // resumed session carries its own answer, whatever the URL says now.
  const programId = existing
    ? (existing.programId ?? null)
    : new URLSearchParams(history.location.search).get("program");
  const program = programId ? findProgram(S, programId) : null;
  const [flat] = useState(() => !!S.profile.inclineOverride);
  const easy = useEasyTreadmill();

  // the protocol is frozen on mount: editing speeds mid-run must not reshuffle the session
  const [phases] = useState<TreadmillPhase[]>(() =>
    existing?.phases?.length
      ? existing.phases
      : program
        ? programToPhases(program, flat)
        : tmProtocol(S, !!easy)
  );
  // Frozen at mount, like the protocol: the window the phone's counter is measured against has
  // to be the walk itself, not the moment the finish button happened to be pressed.
  const [startedAt] = useState(() => existing?.startedAt || new Date().toISOString());
  /** Where a resumed session has got to. The machine did not wait for the page. */
  const [initial] = useState(() =>
    existing
      ? treadmillPosition(existing, Date.now())
      : { idx: 0, leftMs: phases[0].secs * 1000, over: false }
  );
  const [idx, setIdx] = useState(initial.idx);
  const [finished, setFinished] = useState(false);

  const { remaining, running, start, pause, resume, stop, endsAt } = useTimer(undefined, handleEnd);

  /** Read at call time: ending the session clears it, and a stop must name it first. */
  const sid = () => useKaya.getState().session?.sid;
  const announce = () => {
    if (announced.current) return;
    announced.current = true;
    pingSession("resume", Date.now(), sid(), { kind: "treadmill", stride: strideMeters(S) });
  };

  useEffect(() => {
    if (other) history.replace(other.route);
  }, [other, history]);

  useEffect(() => {
    if (other) return;
    // A fresh session begins only from a Start tap's push. Landing here any other way with no
    // record — a reload, a deep link, the browser's fallback after an `intent:` it could not
    // hand over — must not start one: that is how a finished run began again from phase one
    // instead of resting on its done screen. (ADR-035)
    if (!existing && history.action !== "PUSH") {
      history.replace(origin);
      return;
    }
    void setWakeLock(true);
    if (!existing) {
      const now = Date.now();
      const s = beginSession({
        kind: "treadmill",
        route: "/treadrun" + (programId ? `?program=${programId}` : ""),
        startedAt,
        phases,
        programId,
        idx: 0,
        since: now,
        phaseEndsAt: now + phases[0].secs * 1000,
      });
      // Exact window edges, rather than whatever the 15-minute sampling happened to catch — and
      // stamped with this session's own start, so the reading is the edge rather than just
      // inside it.
      pingSession("start", Date.parse(startedAt), s.sid, { kind: "treadmill", stride: strideMeters(S) });
      start(phases[0].secs);
      notifyNext(0);
    } else if (initial.over) {
      // Every phase ended while the page was away: the session ran its course on the machine,
      // and it ended when the last phase did — not now. The stop ping below is dropped by the
      // browser (no gesture); the next tap sends it.
      stopPending.current = true;
      finish("timer", phases.length, treadmillEndsAt(existing) ?? Date.now());
    } else if (existing.since === null) {
      // Paused when the page went away: load the remainder without running it.
      start(initial.leftMs / 1000);
      pause();
      patchSession((s) => {
        s.idx = initial.idx;
        s.since = null;
        s.phaseEndsAt = null;
        s.phaseLeftMs = initial.leftMs;
      });
    } else {
      // The record said where the session was when the page left; the machine has moved on
      // since, so the record moves with it — or the next resume would rewind to here.
      start(initial.leftMs / 1000);
      const ends = endsAt();
      patchSession((s) => {
        s.idx = initial.idx;
        s.since = Date.now();
        s.phaseEndsAt = ends;
        s.phaseLeftMs = undefined;
      });
      notifyNext(initial.idx);
    }
    // No cancel of "tm" here: a minimised run is still going on the machine, and the queued cue
    // is what marks the next phase once the screen is off. Every real exit cancels it.
    return () => {
      stop();
      void setWakeLock(false);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  function notifyNext(n: number) {
    const nx = phases[n + 1];
    if (nx) scheduleNotification("tm", endsAt(), "Treadmill: " + nx.l, "Set " + nx.s.toFixed(1) + " km/h");
  }

  /** Moves the record on to phase `n`, running from now. */
  function recordPhase(n: number) {
    const now = Date.now();
    patchSession((s) => {
      s.idx = n;
      s.since = now;
      s.phaseEndsAt = now + phases[n].secs * 1000;
      s.phaseLeftMs = undefined;
    });
  }

  function finish(how: "timer" | "partial", upto: number, endedAtMs = Date.now()) {
    setFinished(true);
    stop();
    cancelNotification("tm");
    beep(1318, 400, 0.2, S.profile.sound);
    buzz([200, 100, 200, 100, 200], S.profile.vibrate);
    void setWakeLock(false);

    // A program's distance is its own, not the Kaya protocol's — which is the whole reason for
    // transcribing it, and what makes the session trustworthy for stride calibration.
    const t = tmTotals(S, phases, how === "partial" ? upto : phases.length);
    // The window's far edge is when the session ended, which for one that finished while the
    // page was away is earlier than now. Logging "now" would measure the whole evening as the
    // walk and teach the stride from it.
    const at = new Date(endedAtMs).toISOString();
    update((d) => {
      if (!d.logs[today]) d.logs[today] = { done: {}, treadmill: null };
      d.logs[today].treadmill = {
        protocol: program ? "program:" + program.name : how,
        startedAt,
        at,
        kcal: t.kcal,
        km: t.km,
        steps: t.steps,
        // Kaya drove the timing from a profile the user transcribed, so the distance is as good
        // as the guided protocol's — good enough to teach the stride.
        kmFromConsole: program ? true : undefined,
        mins: Math.round(phases.reduce((a, p2) => a + p2.secs, 0) / 60),
      };
      // seed from the remembered answer; the Treadmill screen can still correct it
      if (typeof d.logs[today].treadmillPhoneOnBody !== "boolean")
        d.logs[today].treadmillPhoneOnBody = d.profile.phoneOnBodyDefault;
    });
    checkBadges();
    pingSession("stop", endedAtMs, sid());
    endSession();
  }

  /** The stop the mount could not send, from a tap that can. */
  const settle = () => {
    if (!stopPending.current) return;
    stopPending.current = false;
    pingSession("stop");
  };

  function handleEnd() {
    const n = idx + 1;
    if (n >= phases.length) {
      finish("timer", n);
      return;
    }
    const ph = phases[n];
    beep(ph.c === "brisk" ? 1046 : 660, 300, 0.2, S.profile.sound);
    buzz([200, 100, 200], S.profile.vibrate);
    setIdx(n);
    start(ph.secs);
    recordPhase(n);
    notifyNext(n);
  }

  const togglePause = () => {
    announce();
    if (running) {
      const left = Math.max(0, endsAt() - Date.now());
      pause();
      cancelNotification("tm");
      patchSession((s) => {
        s.since = null;
        s.phaseEndsAt = null;
        s.phaseLeftMs = left;
      });
    } else {
      resume();
      const ends = endsAt();
      patchSession((s) => {
        s.since = Date.now();
        s.phaseEndsAt = ends;
        s.phaseLeftMs = undefined;
      });
      notifyNext(idx);
    }
  };

  const skip = () => {
    announce();
    stop();
    cancelNotification("tm");
    const n = idx + 1;
    if (n >= phases.length) {
      finish("partial", n);
      return;
    }
    setIdx(n);
    start(phases[n].secs);
    recordPhase(n);
    notifyNext(n);
  };

  /** End, from the More menu. Its inline confirm is the question the old `window.confirm` asked. */
  const abandon = () => {
    stop();
    cancelNotification("tm");
    void setWakeLock(false);
    // The counter app was told the session started; it has to be told it ended, or it holds the
    // session and its notification until something else does.
    pingSession("stop", Date.now(), sid());
    endSession();
    // `replace` on every way out: a pushed page stays mounted in Ionic and is handed back for
    // the next push to the same path, which would be this session's screen. A replace unmounts.
    history.replace(origin);
  };

  /** Leaves the run going: the machine does not stop because the page did, and the record
   *  already says where it will have got to. The LiveBar takes over. No ping — nothing ended. */
  const minimise = () => history.replace(origin);

  const soundItem = {
    label: "Sound",
    checked: S.profile.sound,
    onClick: () =>
      update((d) => {
        d.profile.sound = !d.profile.sound;
      }),
  };
  const title = program ? program.name : "Treadmill";

  if (finished) {
    const d = S.logs[today] ? S.logs[today].treadmill : null;
    return (
      <PlayerShell
        surface="done"
        title={`${title} · ${dayLabel(today)}`}
        children={<HowWasAsk ds={today} sub={`${title} · one tap`} talk />}
        onMinimise={() => {
          settle();
          history.replace(origin);
        }}
        finished={{
          heading: "Treadmill done",
          sub: "Calf stretch 30 s/side, standing hip-flexor 30 s/side.",
          stats: [
            { figure: d ? d.kcal : null, label: "kcal", estimated: true },
            {
              figure: d ? d.km.toFixed(1) : null,
              label: "km",
              estimated: !program,
              tag: program ? <Tag tone="console" /> : undefined,
            },
            { figure: d ? d.steps : null, label: "steps", estimated: true },
          ],
          line: "Measured steps arrive from the phone on the next sync.",
          quote: quote(),
          onDone: () => {
            settle();
            history.replace("/today");
          },
          // The console's own distance is what teaches the stride; it is entered on the
          // treadmill page. A transcribed program already carries it.
          secondary: program
            ? null
            : {
                label: "Add distance from the console",
                icon: I.edit,
                onClick: () => {
                  settle();
                  history.replace("/train/treadmill");
                },
              },
        }}
      />
    );
  }

  const cur = phases[idx] || phases[phases.length - 1];
  const next = phases[idx + 1];
  // Against the phase's own length, not the timer's: a resumed phase starts the timer with only
  // what was left, and the ring should still show how far through the phase that is.
  const frac = cur.secs ? Math.min(1, Math.max(0, 1 - remaining / (cur.secs * 1000))) : 0;
  const live = tmTotals(S, phases, idx, frac);
  const surface = !running ? "ink" : cur.c === "brisk" ? "work" : cur.c === "easy" ? "rest" : "ink";

  let totalLeft = remaining;
  for (let k = idx + 1; k < phases.length; k++) totalLeft += phases[k].secs * 1000;
  let done = cur.secs * 1000 - remaining;
  for (let k = 0; k < idx; k++) done += phases[k].secs * 1000;

  return (
    <PlayerShell
      surface={surface}
      title={title}
      onMinimise={minimise}
      elapsed={fmtClock(Math.max(0, done))}
      menu={[soundItem]}
      end={{
        label: "End session",
        question: "End the treadmill session? Progress so far won't be logged.",
        onConfirm: abandon,
      }}
      segments={
        <SegmentBar
          count={phases.length}
          current={idx}
          progress={frac}
          weights={phases.map((ph) => ph.secs)}
          onSurface
        />
      }
      line={`${running ? "" : "Paused · "}Phase ${idx + 1} of ${phases.length} · ${fmtClock(totalLeft)} to go`}
      ring={{
        pct: frac,
        figure: fmtClock(remaining),
        unit: running ? `left in ${cur.l.toLowerCase()}` : "paused",
        label: `${fmtClock(remaining)} left in this ${cur.l.toLowerCase()} phase`,
      }}
      name={cur.l}
      pair={[
        { figure: cur.s.toFixed(1), unit: "km/h" },
        { figure: String(cur.inc || 0), unit: "% incline" },
      ]}
      next={next ? `Next · ${next.l} · ${fmtClock(next.secs * 1000)} at ${next.s.toFixed(1)} km/h` : null}
      stats={[
        { figure: live.kcal, label: "kcal", estimated: true },
        { figure: live.km.toFixed(2), label: "km", estimated: true },
        { figure: live.steps, label: "steps", estimated: true },
      ]}
      centre={{
        label: running ? "Pause" : "Resume",
        icon: running ? I.pause : I.play,
        onClick: togglePause,
      }}
      right={{ label: "Skip phase", icon: I.skip, onClick: skip }}
    />
  );
}
